'use client';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/_components/UIComponents/Tooltip';
import type { OnChainIdentity } from '@/types';
import VerifiedBadge from '@/_components/VerifiedBadge/VerifiedBadge';

interface IdentityTooltipProps {
	/** The identity data to display */
	identity: OnChainIdentity;
	/** The content that triggers the tooltip */
	children: React.ReactNode;
}

// SVG Icons as inline components for each field type
const Icons = {
	user: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' />
			<circle
				cx='12'
				cy='7'
				r='4'
			/>
		</svg>
	),
	legal: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
			<polyline points='14 2 14 8 20 8' />
			<line
				x1='16'
				y1='13'
				x2='8'
				y2='13'
			/>
			<line
				x1='16'
				y1='17'
				x2='8'
				y2='17'
			/>
		</svg>
	),
	web: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<circle
				cx='12'
				cy='12'
				r='10'
			/>
			<line
				x1='2'
				y1='12'
				x2='22'
				y2='12'
			/>
			<path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
		</svg>
	),
	twitter: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='currentColor'
		>
			<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
		</svg>
	),
	email: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' />
			<polyline points='22,6 12,13 2,6' />
		</svg>
	),
	chain: (
		<svg
			width='14'
			height='14'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' />
			<path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' />
		</svg>
	)
};

/**
 * Row component for displaying an identity field with icon and value
 */
function IdentityRow({
	icon,
	label,
	value,
	isLink = false,
	href,
	className = ''
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
	isLink?: boolean;
	href?: string;
	className?: string;
}) {
	return (
		<div className={`flex items-center gap-2 py-1 ${className}`}>
			<span className='text-muted-foreground flex-shrink-0'>{icon}</span>
			<span className='text-muted-foreground min-w-[60px] text-xs'>{label}</span>
			{isLink && href ? (
				<a
					href={href}
					target='_blank'
					rel='noopener noreferrer'
					className='max-w-[200px] truncate text-sm text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300'
					onClick={(e) => e.stopPropagation()}
				>
					{value}
				</a>
			) : (
				<span className='text-foreground max-w-[200px] truncate text-sm'>{value}</span>
			)}
		</div>
	);
}

/**
 * Identity tooltip component that displays comprehensive identity information
 * Shows display name, legal name, website, twitter, email, verification status, and chain source
 */
function IdentityTooltip({ identity, children }: IdentityTooltipProps) {
	const hasExtraInfo = identity.legal || identity.web || identity.twitter || identity.email;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent
				side='top'
				sideOffset={8}
				hideArrow
				className='border-border bg-popover max-w-[320px] rounded-lg border p-3 shadow-xl'
			>
				{/* Header with display name and verification */}
				<div className='border-border mb-2 flex items-center gap-2 border-b pb-2'>
					<span className='text-muted-foreground'>{Icons.user}</span>
					<span className='text-foreground text-base font-semibold'>{identity.displayName}</span>
					{identity.isVerified && identity.judgement && (
						<VerifiedBadge
							judgement={identity.judgement}
							size={18}
						/>
					)}
				</div>

				{/* Identity fields */}
				<div className='space-y-0.5'>
					{identity.legal && (
						<IdentityRow
							icon={Icons.legal}
							label='Legal'
							value={identity.legal}
						/>
					)}
					{identity.web && (
						<IdentityRow
							icon={Icons.web}
							label='Website'
							value={identity.web}
							isLink
							href={identity.web.startsWith('http') ? identity.web : `https://${identity.web}`}
						/>
					)}
					{identity.twitter && (
						<IdentityRow
							icon={Icons.twitter}
							label='Twitter'
							value={identity.twitter.startsWith('@') ? identity.twitter : `@${identity.twitter}`}
							isLink
							href={`https://x.com/${identity.twitter.replace('@', '')}`}
						/>
					)}
					{identity.email && (
						<IdentityRow
							icon={Icons.email}
							label='Email'
							value={identity.email}
							isLink
							href={`mailto:${identity.email}`}
						/>
					)}
				</div>

				{/* Footer with chain source and verification status */}
				<div className='border-border mt-2 flex items-center justify-between gap-3 border-t pt-2'>
					<div className='text-muted-foreground flex items-center gap-1.5 text-xs'>
						{Icons.chain}
						<span>{identity.source === 'polkadot' ? 'Polkadot' : 'Kusama'} People Chain</span>
					</div>
					{identity.isVerified && identity.judgement && (
						<span
							className={`rounded-full px-2 py-0.5 text-xs ${
								identity.judgement === 'KnownGood' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
							}`}
						>
							{identity.judgement}
						</span>
					)}
				</div>

				{/* No extra info message */}
				{!hasExtraInfo && (
					<p className='text-muted-foreground mt-1 text-xs'>Only display name is set for this identity.</p>
				)}
			</TooltipContent>
		</Tooltip>
	);
}

export default IdentityTooltip;
export type { IdentityTooltipProps };
